/**
 * Shared formatting for exporting an AI summary.
 *
 * An AI summary is authored as markdown, and every surface that lets a user take
 * it somewhere needs the same three shapes: the markdown itself, a readable
 * plain-text rendering, and a filename. Those conversions were being re-derived
 * per call site (the Slack follow-up modal had its own `plainTextFallback`, the
 * speech hook its own filename slug), so a fix in one never reached the others.
 *
 * Pure + isomorphic — no DOM, no server-only imports — so the clipboard path in
 * the browser and the file/PDF path on the server produce identical text.
 */

/** The export formats offered to a user. */
export type SummaryExportFormat = "markdown" | "text" | "pdf" | "google-doc" | "audio";

export const SUMMARY_EXPORT_EXTENSION: Record<
  Exclude<SummaryExportFormat, "google-doc">,
  string
> = {
  markdown: "md",
  text: "txt",
  pdf: "pdf",
  audio: "mp3",
};

/**
 * Render markdown as readable plain text.
 *
 * Not a markdown parser: it removes the syntax a reader does not want in a
 * pasted-into-email version while KEEPING the structure they do — list bullets
 * become "• ", headings stay on their own line, table rows become dash-joined
 * cells. Link text survives and its URL is kept in parentheses, because a
 * summary's links are usually the useful part (a deal, a ticket).
 */
export function markdownToPlainText(markdown: string): string {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
  const out: string[] = [];
  let inFence = false;

  for (const raw of lines) {
    const line = raw.replace(/\t/g, "  ");

    if (/^\s*(?:```|~~~)/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) {
      out.push(line);
      continue;
    }

    // Horizontal rule → blank separator.
    if (/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      out.push("");
      continue;
    }

    // Table row → "cell — cell", dropping the delimiter row.
    if (/^\s*\|.*\|\s*$/.test(line)) {
      const cells = line
        .trim()
        .replace(/^\||\|$/g, "")
        .split("|")
        .map((cell) => cell.trim())
        .filter((cell) => cell.length > 0 && !/^:?-{3,}:?$/.test(cell));
      if (cells.length > 0) out.push(inlineToText(cells.join(" — ")));
      continue;
    }

    const heading = /^(\s*)#{1,6}\s+(.*)$/.exec(line);
    if (heading) {
      out.push(inlineToText(heading[2]!.trim()));
      continue;
    }

    const quote = /^(\s*)>+\s?(.*)$/.exec(line);
    if (quote) {
      out.push(`${quote[1]}${inlineToText(quote[2]!.trim())}`);
      continue;
    }

    const listItem = /^(\s*)(?:[-*+]|\d+[.)])\s+(.*)$/.exec(line);
    if (listItem) {
      const indent = listItem[1] ?? "";
      out.push(`${indent}• ${inlineToText(listItem[2]!.trim())}`);
      continue;
    }

    out.push(inlineToText(line));
  }

  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/** Strip inline markdown: emphasis, inline code, images; keep link text + URL. */
function inlineToText(text: string): string {
  return text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1") // image → alt text
    .replace(/\[([^\]]*)\]\(([^)\s]+)[^)]*\)/g, (_full, label: string, url: string) =>
      label.trim().length > 0 ? `${label} (${url})` : url
    )
    .replace(/(\*\*|__)(.*?)\1/g, "$2")
    .replace(/(^|[\s(])(\*|_)(?=\S)(.*?)\S\2/g, (full) => full.replace(/[*_]/g, ""))
    .replace(/`([^`]*)`/g, "$1")
    .replace(/~~(.*?)~~/g, "$1");
}

/**
 * A filesystem-safe base name for an exported summary.
 *
 * `company-briefing-acme-corp` rather than `Company briefing — Acme Corp`, so
 * the file survives every OS, Slack upload and email attachment unchanged.
 */
export function summaryExportBaseName(title: string, subject?: string | null): string {
  const parts = [title, subject].filter(
    (part): part is string => typeof part === "string" && part.trim().length > 0
  );
  const slug = parts
    .join(" ")
    .toLowerCase()
    .normalize("NFKD") // decompose accents so the collapse below drops the marks
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
  return slug.length > 0 ? slug : "ai-summary";
}

/** `company-briefing.md` — the name a download lands under. */
export function summaryExportFilename(
  title: string,
  format: Exclude<SummaryExportFormat, "google-doc">,
  subject?: string | null
): string {
  return `${summaryExportBaseName(title, subject)}.${SUMMARY_EXPORT_EXTENSION[format]}`;
}

/**
 * The document a text/markdown export contains: a title line, an
 * AI-generated + generated-on note, then the body.
 *
 * The provenance note is not decoration — an exported summary gets pasted into
 * email and forwarded, and a reader who does not know it was model-written (and
 * when) can mistake it for a verified record.
 */
export function buildSummaryDocument(input: {
  title: string;
  body: string;
  format: "markdown" | "text";
  /** Rendered date string, already in the reader's business timezone. */
  generatedAt: string;
  sourceUrl?: string | null;
}): string {
  const bodyText =
    input.format === "markdown" ? input.body.trim() : markdownToPlainText(input.body);
  const note = `AI-generated summary, exported from Hub on ${input.generatedAt}. Review for accuracy before relying on it.`;

  if (input.format === "markdown") {
    const lines = [`# ${input.title}`, "", `_${note}_`, ""];
    if (input.sourceUrl) lines.push(`[Open in Hub](${input.sourceUrl})`, "");
    lines.push("---", "", bodyText);
    return lines.join("\n");
  }

  const lines = [input.title, "=".repeat(Math.min(input.title.length, 60)), "", note, ""];
  if (input.sourceUrl) lines.push(`Open in Hub: ${input.sourceUrl}`, "");
  lines.push(bodyText);
  return lines.join("\n");
}
